import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container, Spinner, Button, Alert, Card, CardBody } from "reactstrap";
import {
	getDealerInsuranceInfo,
	getPartnerLinks,
	getPartnerSend,
} from "api/DealerIntegrationApi";
import { useFromLocations, useFromUser } from "stores/LocalStorageHelper";
import { showApiError } from "utils/errorRoutingUtils";
import { retrieveFailAlert, dealerExpireAlert } from "utils/alertUtils";
import dayjs from "dayjs";
import {
	getAllStandardForms,
	getAllStateForms,
	getAllCustomForms,
	getAllInventoryForms,
	getAllTradeForms,
	getAllInventoryReports,
	getAllManagementReports,
	getAllSalesReports,
	getAllCustomReports,
	getAllBHPHReports,
	getAllBHPHForms,
	getAllOkiForms,
	getAllXlsReports,
	// getAllLHPHReports,
	// getAllLHPHForms,
	// getAllRTOReports,
	// getAllRTOForms,
} from "api/FormsAPI";
import { getDealerTempList } from "api/SettingsAPI";
import {
	getDealerMakeList,
	getDealerModelList,
	getVehicleSettings,
} from "api/InventoryAPI";
import { getDigitzsMerchant } from "api/PaymentsAPI";
import { getVehicleTypes } from "api/TecAssuredAPI";
import { PATHS } from "constants/Constants";
import { DC_Login_Logo, DC_CompanyName, BrandCode } from "../../../config";

/**
 * DataLoadingScreen component - Loading screen that fetches all necessary data before routing to Dashboard
 * @returns {JSX.Element} - Rendered DataLoadingScreen component
 */
const DataLoadingScreen = () => {
	const history = useHistory();
	const location = useLocation();
	const locationID = useFromUser("locationID");
	const dealerID = useFromUser("dealerID");
	const expDate = useFromLocations("expirationDate");
	const [loadingMessage, setLoadingMessage] = useState("Loading resources...");
	const [hasError, setHasError] = useState(false);
	const [errorDetails, setErrorDetails] = useState("");
	const [retryCount, setRetryCount] = useState(0);
	const MAX_RETRY_ATTEMPTS = 3;

	// Check if this is a refresh request
	const isRefresh = location.state?.refresh === true;

	useEffect(() => {
		const loadAllResources = async () => {
			setHasError(false);
			setErrorDetails("");
			setLoadingMessage("Loading resources...");

			try {
				// First batch: TecAssured vehicle types
				setLoadingMessage("Loading vehicle types...");
				try {
					const tecRes = await getVehicleTypes(locationID);
					sessionStorage.setItem(
						"tecAssuredVehicleTypes",
						JSON.stringify(tecRes.data)
					);
				} catch (err) {
					sessionStorage.setItem(
						"tecAssuredVehicleTypes",
						JSON.stringify({ item: [] })
					);
				}

				// Second batch: Dealer insurance, partner info, and merchant data
				setLoadingMessage("Loading dealer information...");
				const [partnerSendRes, partnerLinkRes, merchantRes] = await Promise.all(
					[
						getPartnerSend(locationID),
						getPartnerLinks(locationID),
						getDigitzsMerchant(dealerID, locationID),
					]
				);

				// Check expiration date
				const daysLeft = dayjs(expDate).diff(dayjs(), "day");
				if (daysLeft < 30) {
					dealerExpireAlert(daysLeft < 0 ? 0 : daysLeft);
				}

				// Store dealer partner data
				const partnerSend = partnerSendRes.data.content;
				const partnerLink = partnerLinkRes.data.content.dealerPartnerLink;
				const isCreditSignup = partnerLinkRes.data.content.isCreditSignup;
				const digitzsMerchant =
					merchantRes.data.content === null
						? { merchantId: null }
						: merchantRes.data.content;

				sessionStorage.setItem(
					"dealerPartnerSend",
					JSON.stringify(partnerSend)
				);
				sessionStorage.setItem(
					"dealerPartnerLink",
					JSON.stringify({ ...partnerLink, isCreditSignup })
				);
				sessionStorage.setItem(
					"digitzsMerchant",
					JSON.stringify(digitzsMerchant)
				);

				// Third batch: Forms, reports, and dealer data
				if (dealerID && locationID) {
					setLoadingMessage("Loading forms and reports...");
					const params = { dealerID, locationID };
					const [
						standardFormsRes,
						stateFormsRes,
						customFormsRes,
						inventoryFormsRes,
						tradeFormsRes,
						inventoryReportsRes,
						managementReportsRes,
						salesReportsRes,
						customReportsRes,
						bhphReportsRes,
						bhphFormsRes,
						// lhphReportsRes,
						// lhphFormsRes,
						// rtoReportsRes,
						// rtoFormsRes,
						okiFormsRes,
						dealerTempRes,
						makesRes,
						modelsRes,
						vehicleSettingsRes,
						xlsReportsRes,
					] = await Promise.all([
						getAllStandardForms(params),
						getAllStateForms(params),
						getAllCustomForms(params),
						getAllInventoryForms(params),
						getAllTradeForms(params),
						getAllInventoryReports(params),
						getAllManagementReports(params),
						getAllSalesReports(params),
						getAllCustomReports(params),
						getAllBHPHReports(params),
						getAllBHPHForms(params),
						// getAllLHPHReports(params),
						// getAllLHPHForms(params),
						// getAllRTOReports(params),
						// getAllRTOForms(params),
						getAllOkiForms(params),
						getDealerTempList(locationID),
						getDealerMakeList(dealerID, locationID),
						getDealerModelList(dealerID, locationID),
						getVehicleSettings(locationID),
						getAllXlsReports(params),
					]);

					// Store forms and reports
					const formsReports = {
						standardForms: standardFormsRes.data.content,
						stateForms: stateFormsRes.data.content,
						customForms: customFormsRes.data.content,
						inventoryForms: inventoryFormsRes.data.content,
						tradeForms: tradeFormsRes.data.content,
						inventoryReports: inventoryReportsRes.data.content,
						managementReports: managementReportsRes.data.content,
						salesReports: salesReportsRes.data.content,
						customReports: customReportsRes.data.content,
						bhphReports: bhphReportsRes.data.content,
						bhphForms: bhphFormsRes.data.content,
						// lhphReports: lhphReportsRes.data.content,
						// lhphForms: lhphFormsRes.data.content,
						// rtoReports: rtoReportsRes.data.content,
						// rtoForms: rtoFormsRes.data.content,
						okiForms: okiFormsRes.data.content,
						xlsReports: xlsReportsRes.data.content,
					};

					const makes = makesRes.data.content.map((make) => ({
						label: make.make,
						value: make.make,
						obj: make,
					}));

					const models = modelsRes.data.content.map((model) => ({
						label: model.model,
						value: model.model,
						obj: model,
					}));

					sessionStorage.setItem(
						"dealersList",
						JSON.stringify(dealerTempRes.data)
					);
					sessionStorage.setItem("formsReports", JSON.stringify(formsReports));
					sessionStorage.setItem("makes", JSON.stringify(makes));
					sessionStorage.setItem("models", JSON.stringify(models));
					sessionStorage.setItem(
						"inventoryDefaults",
						JSON.stringify(vehicleSettingsRes.data.content)
					);
				}

				// All data loaded successfully, navigate to dashboard
				setLoadingMessage("Complete! Redirecting...");
				setTimeout(() => {
					history.push(PATHS.DASHBOARD);
				}, 500);
			} catch (err) {
				console.error("Error loading resources:", err);
				setHasError(true);
				setErrorDetails(
					err?.response?.data?.message ||
						err?.message ||
						"Unknown error occurred"
				);
				if (!err.isGeneralError) {
					showApiError(err, retrieveFailAlert);
				}
			}
		};

		loadAllResources();
	}, [locationID, dealerID, retryCount]);

	const handleRetry = () => {
		setRetryCount((prev) => prev + 1);
	};

	const handleLogout = () => {
		// Clear session storage and redirect to login
		sessionStorage.clear();
		history.push(PATHS.LOGIN);
	};

	return (
		<div className="login-page">
			<Card className="card-login">
				<img
					src={DC_Login_Logo}
					alt={DC_CompanyName + " Logo"}
					className="logo mt-3"
				/>
				<CardBody className="px-4 d-flex flex-column align-items-center justify-content-center">
					{!hasError ? (
						<>
							<Spinner
								color="primary"
								style={{ width: "3rem", height: "3rem" }}
							/>
							<h3 className="mt-4 text-center">{loadingMessage}</h3>
							{isRefresh && (
								<p className="text-muted text-center">Refreshing data...</p>
							)}
						</>
					) : (
						<>
							<Alert
								color="info"
								className="text-center"
								style={{ maxWidth: "600px", width: "100%" }}
							>
								<h4 className="alert-heading">Failed to Load Resources</h4>
								<p>{errorDetails}</p>
								{retryCount >= MAX_RETRY_ATTEMPTS && (
									<>
										<hr />
										<p className="mb-0">
											<strong>Maximum retry attempts reached.</strong>
											<br />
											Please log out and log back in. If the problem persists,
											contact support.
										</p>
									</>
								)}
							</Alert>
							<div className="d-flex justify-content-center gap-3">
								{retryCount < MAX_RETRY_ATTEMPTS ? (
									<Button color="primary" onClick={handleRetry}>
										Retry ({retryCount + 1}/{MAX_RETRY_ATTEMPTS})
									</Button>
								) : (
									<Button color="primary" onClick={handleLogout}>
										Log Out
									</Button>
								)}
							</div>
						</>
					)}
				</CardBody>
			</Card>
		</div>
	);
};

export default DataLoadingScreen;
